import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectCurrentUser } from 'store/users/users.selectors'
import { selectOrderFilterId, selectSearchOrders } from 'store/orders/orders.selectors'
import { fetchOrdersRequest } from 'store/orders/orders.actions'
import { OrderFilterId } from 'types/order'
import { UserRole } from 'types/user'

const POLLING_DELAY = 60000

type TCreatePollingProps = {
  orderFilterId: string
  isSearchOrders: string
  currentUserRole?: UserRole
  cb: () => void
}

const createPollingInterval = ({ orderFilterId, currentUserRole, isSearchOrders, cb }: TCreatePollingProps): NodeJS.Timeout | undefined => {
  const isFilterUserOrders = orderFilterId === OrderFilterId.USER_ORDERS
  const isUserManagerOrExpert = currentUserRole === UserRole.MANAGER || currentUserRole === UserRole.EXPERT
  const isDocumentVisible = document.visibilityState === 'visible'
  const isSearchActive = !!isSearchOrders

  if (isFilterUserOrders && isUserManagerOrExpert && isDocumentVisible && !isSearchActive) {
    return setInterval(cb, POLLING_DELAY)
  }
}

export const useOrdersListPolling = () => {
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const isSearchOrders = useSelector(selectSearchOrders)
  const orderFilterId = useSelector(selectOrderFilterId)

  useEffect(() => {
    let pollingIntervalId: NodeJS.Timeout | undefined

    const startPolling = () => {
      pollingIntervalId = createPollingInterval({
        orderFilterId,
        currentUserRole: currentUser?.role,
        isSearchOrders,
        cb: () => dispatch(fetchOrdersRequest({ silent: true })),
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchOrdersRequest({ silent: true }))
        startPolling()
      } else if (pollingIntervalId) {
        clearInterval(pollingIntervalId)
      }
    }

    // run on mount to start polling
    if (document.visibilityState === 'visible') {
      startPolling()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (pollingIntervalId) clearInterval(pollingIntervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [orderFilterId, currentUser, isSearchOrders, dispatch])
}
